import React from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

/**
 * Dialog shown when the backend rejects a create request with HTTP 409
 * because a normalized-name duplicate already exists. Lets the user cancel
 * (so they can reuse the existing entry) or force-create anyway.
 *
 * Props:
 *   - duplicateError: object | null (the response.detail returned by the API)
 *   - onCancel: () => void
 *   - onForceCreate: () => Promise<void> (called when user confirms)
 *   - entityLabel: string (e.g. "categoría", "ubicación", "responsable")
 */
const DuplicateWarningDialog = ({ duplicateError, onCancel, onForceCreate, entityLabel = 'entrada' }) => {
  const open = !!duplicateError;
  const existing = duplicateError?.existing || [];

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onCancel?.()}>
      <AlertDialogContent data-testid="duplicate-warning-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-500">
            <AlertTriangle className="h-5 w-5" />
            Posible duplicado
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                Ya existe {existing.length === 1 ? `una ${entityLabel}` : `${existing.length} ${entityLabel}s`} con el mismo nombre normalizado:
              </p>
              <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                {existing.map((e) => (
                  <div key={e.id} className="flex items-center justify-between gap-2">
                    <span className="font-medium">"{e.name}"</span>
                    <span className="text-xs text-muted-foreground font-mono">
                      id: {e.id?.slice(0, 8)}…
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Te recomendamos <strong>cancelar y reutilizar</strong> la {entityLabel} existente. Si aún así necesitas crear una nueva (por ejemplo, porque pertenece a otro contexto), puedes forzarla.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} data-testid="duplicate-cancel-btn">
            Cancelar y reutilizar
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); onForceCreate?.(); }}
            className="bg-amber-600 hover:bg-amber-500 text-white"
            data-testid="duplicate-force-btn"
          >
            Crear de todas formas
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DuplicateWarningDialog;
